import { useBreakPoint, useIsPageActive, useModal, useRoomImage } from "@hooks";
import {
  useAppSelector,
  getAllMeetingRoomSuggestionsForRequest,
  getIsWorkdayLoading,
  getMeetingRoomRequestState,
  MeetingRoom,
} from "@lib/store";
import { AvailableRoomInformationModal, AvailableRoomsSkeletonLoader, Tile } from "@organisms";
import addMinutes from "date-fns/addMinutes";
import startOfMinute from "date-fns/startOfMinute";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { DEFAULT_NEW_EVENT_DURATION } from "@constants";
import { useTranslation } from "react-i18next";
import { cn } from "@utils";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@lib/infrastructure";
import {
  QueryParamConstants,
  REFRESH_INTERVAL_SECONDS,
  RoomDateTimes,
  useMeetingRoomQueue,
  useMeetingRoomRequestContext,
  useRefreshDataIfStale,
  useScheduleAndSensorStatuses,
} from "@lib/logic";
import { ScheduleDataIcon } from "src/components/molecules/ScheduleDataIcon";
import { SwiperHorizontal } from "@molecules";

const AVAILABLE_ROOMS_TILE_REFRESH_MINUTES = 5;

export const AvailableRoomsTile = ({ time, buildingId }: { time: Date; buildingId: string }) => {
  const bucketedTime = useMemo(() => {
    const nextTime = new Date(time);
    nextTime.setSeconds(0, 0);
    nextTime.setMinutes(
      Math.floor(nextTime.getMinutes() / AVAILABLE_ROOMS_TILE_REFRESH_MINUTES) * AVAILABLE_ROOMS_TILE_REFRESH_MINUTES,
    );

    return nextTime;
  }, [time]);

  const seedRoomDateTimes = useMemo(
    () => ({
      startDateTime: startOfMinute(bucketedTime).toISOString(),
      endDateTime: addMinutes(startOfMinute(bucketedTime), DEFAULT_NEW_EVENT_DURATION).toISOString(),
    }),
    [bucketedTime],
  );

  return (
    <AvailableRoomsTileContent
      key={`${buildingId}:${seedRoomDateTimes.startDateTime}:${seedRoomDateTimes.endDateTime}`}
      buildingId={buildingId}
      seedRoomDateTimes={seedRoomDateTimes}
    />
  );
};

const AvailableRoomsTileContent = ({
  buildingId,
  seedRoomDateTimes,
}: {
  buildingId: string;
  seedRoomDateTimes: RoomDateTimes;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const pageActive = useIsPageActive();
  const workdayLoading = useAppSelector(getIsWorkdayLoading);
  const [roomDateTimes, setRoomDateTimes] = useState(seedRoomDateTimes);
  const activeMeetingRoomRequest = useMeetingRoomRequestContext({
    buildingId,
    selectedFloorId: null,
    startDateTime: roomDateTimes.startDateTime,
    endDateTime: roomDateTimes.endDateTime,
  });
  const { endDateTime, loadRoomsIfStale, shouldLoadRooms, startDateTime } = useMeetingRoomQueue({
    buildingId,
    roomDateTimes,
    selectedFloorId: null,
  });
  const activeRequestState = useAppSelector(state => getMeetingRoomRequestState(state, activeMeetingRoomRequest));
  const allRooms = useAppSelector(state => getAllMeetingRoomSuggestionsForRequest(state, activeMeetingRoomRequest));
  const meetingRoomsAreLoading =
    shouldLoadRooms && (activeRequestState?.allFloorsLoadingStatus === "loading" || !activeRequestState);
  const firstThreeRooms = allRooms.slice(0, 3);
  const { refreshIfStale, refreshLabel } = useRefreshDataIfStale(meetingRoomsAreLoading, REFRESH_INTERVAL_SECONDS, t);

  const isLoading = workdayLoading || (meetingRoomsAreLoading && allRooms.length === 0);

  useEffect(
    function refreshAvailableRoomsIfStale() {
      if (!pageActive) return;

      refreshIfStale(() => {
        const nextRoomDateTimes = loadRoomsIfStale();

        if (nextRoomDateTimes) {
          setRoomDateTimes(nextRoomDateTimes);
        }
      });
    },
    [loadRoomsIfStale, pageActive, refreshIfStale],
  );

  return (
    <Tile
      title={t("FindAMeetingRoom")}
      viewAllAction={() => {
        trackEvent("Home_AvailableRoomsTile__ViewAll");
        navigate(
          buildingId ? `/now/rooms?${QueryParamConstants.hereAndNow.rooms.buildingId}=${buildingId}` : "/now/rooms",
        );
      }}
      titleNote={refreshLabel}>
      {isLoading ? (
        <AvailableRoomsSkeletonLoader columns={3} />
      ) : firstThreeRooms.length ? (
        <RowContainer>
          {buildingId
            ? firstThreeRooms.map(room => (
                <AvailableRoomTile
                  key={room.id}
                  room={room}
                  startTime={startDateTime}
                  endTime={endDateTime}
                  buildingId={buildingId}
                  className={firstThreeRooms.length === 3 ? "col-span-2" : "col-span-3"}
                />
              ))
            : null}
          {firstThreeRooms.length === 1 ? (
            <div className="bg-collaborative-blue-50 rounded-md flex flex-col justify-center items-center w-full aspect-[255/150] h-44 col-span-3">
              <p>{t("NoOtherRoomsAvailable")}</p>
            </div>
          ) : null}
        </RowContainer>
      ) : (
        <div className="bg-collaborative-blue-50 w-full rounded-md flex flex-col justify-center items-center h-[170px]">
          <p>{t("NoRoomsAvailable")}</p>
        </div>
      )}
    </Tile>
  );
};

const RowContainer = ({ children }: PropsWithChildren) => {
  const breakPoint = useBreakPoint();

  return breakPoint === "small" ? (
    <SwiperHorizontal
      className="w-full [&.swiper>div.swiper-wrapper]:p-0"
      spaceBetween={16}>
      {children}
    </SwiperHorizontal>
  ) : (
    <div className="grid grid-cols-6 w-full gap-8">{children}</div>
  );
};

const AvailableRoomTile = ({
  room,
  startTime,
  endTime,
  buildingId,
  className,
}: {
  room: MeetingRoom;
  startTime: string;
  endTime: string;
  buildingId: string;
  className: string;
}) => {
  const { t } = useTranslation();
  const bp = useBreakPoint();
  const { openModal, addPages } = useModal();
  const { floorName, displayName, capacity, email, roomPictureHash, id } = room;
  const { scheduleStatus, sensorStatus } = useScheduleAndSensorStatuses(buildingId, id, startTime, endTime, email);
  const imageURL = useRoomImage(
    email || displayName,
    roomPictureHash,
    buildingId,
    Math.round(48 * window.devicePixelRatio * 4),
  );

  function handleRoomClick(selectedRoom: MeetingRoom, startDateTime: string, endDateTime: string) {
    addPages([
      <AvailableRoomInformationModal
        room={selectedRoom}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
      />,
    ]);

    trackEvent("Home_AvailableRoomsTile__ViewDetails", { roomName: selectedRoom.displayName });
    openModal();
  }

  return (
    <button
      data-testid="HomePage-available-room"
      className={cn("flex flex-col gap-4 rounded-md outline-offset-8 outline-gray-400", className)}
      onClick={() => handleRoomClick(room, startTime, endTime)}>
      {imageURL ? (
        <img
          alt={room.displayName}
          src={imageURL}
          className={cn(`cursor-pointer object-cover rounded-md w-full aspect-[255/150]`)}
        />
      ) : null}
      <div
        className={cn("flex flex-row justify-between gap-2 items-center", {
          "flex-col-reverse items-start": bp === "medium",
        })}>
        <div className="flex flex-col">
          <h4>{displayName}</h4>
          <p>
            {t("meeting:MeetingRoomBasicInfo", {
              count: capacity,
              floorName,
            })}
          </p>
        </div>
        <div className="flex flex-row gap-2">
          {sensorStatus && (
            <ScheduleDataIcon
              data-testid={`HomePageSensorIcon-${floorName}`}
              status={sensorStatus}
              icon="broadcast"
            />
          )}
          {scheduleStatus && (
            <ScheduleDataIcon
              data-testid={`HomePageScheduleDataIcon-${floorName}`}
              status={scheduleStatus}
              icon="calendar"
            />
          )}
        </div>
      </div>
    </button>
  );
};
